import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toSpokenText } from '../utils/spokenText';
import { getToolMessageKey, isDisplayedToolEvent } from '../utils/toolMessages';

import type { ToolStatus } from '../utils/toolMessages';

export type AnnouncedMessage = {
  id: string | number;
  sender: 'user' | 'bot';
  text: string;
  isError?: boolean;
  isStreaming?: boolean;
};

export type AnnouncedToolEvent = {
  id?: string;
  toolName?: string;
  toolType?: string;
  status?: ToolStatus;
  timestamp?: string;
};

export type UseTutorAnnouncementParams = {
  activeToolEvents: AnnouncedToolEvent[];
  conversationId: string | null;
  isVisible: boolean;
  messages: AnnouncedMessage[];
  streamingMessage: AnnouncedMessage | null;
};

type Turn = {
  announcedToolKeys: Set<string>;
  baselineIds: Set<AnnouncedMessage['id']>;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

// i18next 19 vuelve a interpolar el valor de una variable: una respuesta con `{{…}}` (código,
// LaTeX) se recortaría y una con `$t(…)` agotaría la pila. La respuesta no pasa por `t`.
const ANSWER_PLACEHOLDER = '__TUTOR_ANSWER__';

function toolKey(event: AnnouncedToolEvent): string {
  return `${event.id ?? event.toolName}:${event.status}`;
}

export function formatTutorSays(t: Translate, spokenText: string): string {
  return t('chatbot:chat_tutor_says_announcement', { text: ANSWER_PLACEHOLDER }).replace(ANSWER_PLACEHOLDER, () => spokenText);
}

/**
 * Qué debe oír un lector de pantalla del turno del tutor: que está escribiendo, la herramienta que
 * prepara, el error si falla y la respuesta completa en texto plano. Nunca los fragmentos del
 * streaming, que inundarían al lector, ni el historial que se carga al abrir una conversación.
 */
function useTutorAnnouncement({ activeToolEvents, conversationId, isVisible, messages, streamingMessage }: UseTutorAnnouncementParams): string {
  const { t } = useTranslation(['chatbot']);
  const [announcement, setAnnouncement] = useState<string>('');
  const turnRef = useRef<Turn | null>(null);
  const previousConversationIdRef = useRef<string | null>(conversationId);

  useEffect(() => {
    // Al ocultar el chat el motor corta el stream sin respuesta: el turno se descarta para que el
    // siguiente se anuncie, aunque su mensaje y su burbuja lleguen en el mismo render.
    if (!isVisible) {
      turnRef.current = null;
      setAnnouncement('');
      return;
    }

    // Pasar de «sin conversación» a una nueva es el primer mensaje creándola: el turno sigue vivo.
    // Cambiar de una conversación existente a otra sí lo descarta.
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    if (previousConversationId !== null && previousConversationId !== conversationId && turnRef.current) {
      turnRef.current = null;
      setAnnouncement('');
      return;
    }

    if (streamingMessage?.isError) {
      turnRef.current = null;
      setAnnouncement(streamingMessage.text ?? '');
      return;
    }

    if (streamingMessage && !turnRef.current) {
      // Las herramientas que siguen en pantalla son del turno anterior (el motor no las limpia al
      // fallar ni al reintentar): se dan por anunciadas.
      turnRef.current = {
        announcedToolKeys: new Set(activeToolEvents.map(toolKey)),
        baselineIds: new Set(messages.map((message) => message.id)),
      };
      setAnnouncement(t('chatbot:chat_tutor_typing_announcement'));
    }

    const turn = turnRef.current;
    if (!turn) return;

    if (streamingMessage) {
      const newTool = activeToolEvents.find(
        (event) => isDisplayedToolEvent(event) && (event.status === 'started' || event.status === 'failed') && !turn.announcedToolKeys.has(toolKey(event))
      );
      if (newTool?.status) {
        turn.announcedToolKeys.add(toolKey(newTool));
        setAnnouncement(t(getToolMessageKey(newTool.status, newTool.timestamp)));
      }
      return;
    }

    // Sin burbuja de streaming: o la respuesta ya está en `messages`, o llega en el siguiente render
    // (`handleComplete` quita la burbuja y añade el mensaje en dos actualizaciones separadas).
    const currentIds = new Set(messages.map((message) => message.id));
    const isAppendOnly = Array.from(turn.baselineIds).every((id) => currentIds.has(id));
    const newMessages = messages.filter((message) => !turn.baselineIds.has(message.id));
    const answer = [...newMessages].reverse().find((message) => message.sender === 'bot');

    if (isAppendOnly && answer) {
      turnRef.current = null;
      const spokenText = toSpokenText(answer.text);
      setAnnouncement(spokenText ? formatTutorSays(t, spokenText) : '');
      return;
    }

    // El turno terminó sin respuesta (cancelado, chat nuevo, otra conversación): se vacía la región
    // para que el «está escribiendo» del siguiente turno sea un cambio que el lector anuncie.
    if (!isAppendOnly || newMessages.length > 0) {
      turnRef.current = null;
      setAnnouncement('');
    }
  }, [activeToolEvents, conversationId, isVisible, messages, streamingMessage, t]);

  return announcement;
}

export default useTutorAnnouncement;
